import React, { useState, useEffect, useMemo } from 'react'
import api from 'api/api'
import perms from 'lib/perms'
import { isDirectMessageQuest } from 'lib/directMessages'
import Edit from 'components/answers/AnswerControls/Edit'
import CopyButton from 'components/shared/CopyButton'
import ShareTray from 'components/shared/ShareTray'
import WithTooltip from 'components/shared/WithTooltip'
import { formatSatsForBsvUnit } from 'components/shared/useBsvDisplay'
import { formatUsdValue, satsToUsd } from 'api/useBsvExchangeRate'
import { SATOSHI_UNIT } from 'lib/bsv-util'
import styles from 'components/answers/answer-controls.module.scss'
import cn from 'classnames'
import * as cache from 'state/cache'
import useStore from 'state/knovStore'
import useAnswerControls from 'components/answers/useAnswerControls'
import ExtendedOptions from 'components/quests/ExtendedOptions'
import queryClient from 'api/queryClient'
import useGetUser from 'refactor/hooks/api/useGetUser'
import { hasNativeCapability, shareTextOrUrlNative } from 'utils/nativeShare'
import {
    buildShareDestination,
    buildShareEarningsText,
    getAnswerExternalShareUrl,
    getAnswerShareCardUrl,
    resolveAnswerShareLaunchSurface,
    selectShareEarningsLabel,
} from 'utils/shareUtils'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import {
    Archive,
    ArchiveRestore,
    Copy,
    Film,
    Paintbrush,
    Play,
    Send,
    Star,
    Trash2,
    UserCheck,
    UserPlus,
    X,
} from 'lucide-react'
import { getAnswerDisplayText } from 'components/answers/answerDisplayText'
import { canLaunchExistingPostMediaAction } from 'components/answers/postMediaActions'
import { isMobile } from 'lib/device'

const ANSWER_SHARE_TITLE = 'Check out this post on Treechat'
// The only card format offered in the tray; square/story were retired.
const SHARE_CARD_PRESET = 'landscape'

// Clipboard images must be PNG (Chromium rejects image/jpeg ClipboardItems),
// so the JPEG card re-encodes through a canvas. The ClipboardItem takes the
// encode promise directly: Safari only honors writes that start inside the
// user gesture's task. Exported for the other card trays (daily earnings).
export const copyShareCardToClipboard = async jpegBlob => {
    if (
        typeof navigator === 'undefined' ||
        !navigator.clipboard?.write ||
        typeof ClipboardItem === 'undefined'
    ) {
        return false
    }

    try {
        const pngBlob = (async () => {
            const bitmap = await createImageBitmap(jpegBlob)
            try {
                const canvas = document.createElement('canvas')
                canvas.width = bitmap.width
                canvas.height = bitmap.height
                canvas.getContext('2d').drawImage(bitmap, 0, 0)
                return await new Promise((resolve, reject) => {
                    canvas.toBlob(
                        blob => (blob ? resolve(blob) : reject(new Error('PNG encode failed'))),
                        'image/png',
                    )
                })
            } finally {
                bitmap.close?.()
            }
        })()
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })])
        return true
    } catch (error) {
        console.error('Failed to copy image card to clipboard', error)
        return false
    }
}

// Unit-independent sats for external audiences — the sharer's bsvUnit display
// preference shouldn't decide what recipients read.
const formatShareSats = sats =>
    formatSatsForBsvUnit(sats, SATOSHI_UNIT, null, { compactSats: true })

const shareSatsEarned = answer => Number(answer?.bsv_upvalue_amt || 0)

const shareEarningsLabel = (answer, exchangeRate) => {
    const satsEarned = shareSatsEarned(answer)
    if (satsEarned <= 0) return ''

    const usdEarned = satsToUsd(satsEarned, exchangeRate)
    return selectShareEarningsLabel({
        formattedSatsEarned: formatShareSats(satsEarned),
        usdEarned,
        formattedUsdEarned: formatUsdValue(usdEarned),
    })
}

export function buildShareTitle(answer) {
    const author = (
        answer?.virtual_user_name ||
        answer?.user?.name ||
        answer?.user_name ||
        ''
    ).trim()
    return author ? `${author} on Treechat` : ANSWER_SHARE_TITLE
}

const buildShareText = (answer, exchangeRate) =>
    buildShareEarningsText(shareEarningsLabel(answer, exchangeRate))

// Shared share launcher: desktop uses Treechat's destination tray, while
// mobile prefers its platform share surface. closeMenu runs before either
// surface opens.
export async function launchAnswerShare({
    answer,
    copyContent: _copyContent,
    getPublicLink,
    openShareTray,
    closeMenu = null,
}) {
    const directShareUrl = getAnswerExternalShareUrl(answer)
    const hasNativeShare = hasNativeCapability('nativeShare')
    const browserShare =
        typeof navigator !== 'undefined' && typeof navigator.share === 'function'
            ? payload => navigator.share(payload)
            : null
    let launchSurface = resolveAnswerShareLaunchSurface({
        isMobile,
        hasShareTray: Boolean(openShareTray),
        hasNativeShare,
        hasBrowserShare: Boolean(browserShare),
        hasDirectShareUrl: Boolean(directShareUrl),
    })

    if (launchSurface === 'tray') {
        closeMenu?.()
        openShareTray(directShareUrl)

        const trackedShareUrl = await getPublicLink?.()
        openShareTray(trackedShareUrl || directShareUrl, {
            preserveClosed: true,
            resolved: true,
        })
        return
    }

    const resolvedShareUrl = (await getPublicLink?.()) || directShareUrl
    if (!resolvedShareUrl) return

    const sharePayload = buildShareDestination({
        destination: 'native',
        shareUrl: resolvedShareUrl,
        shareText: buildShareText(answer, useStore.getState().exchangeRate),
    }).payload

    if (launchSurface === 'native') {
        closeMenu?.()

        try {
            await shareTextOrUrlNative(sharePayload)
            return
        } catch (error) {
            console.error('Failed to open native share sheet, falling back to share tray', error)
            launchSurface = resolveAnswerShareLaunchSurface({
                isMobile,
                hasShareTray: Boolean(openShareTray),
                hasBrowserShare: Boolean(browserShare),
                hasDirectShareUrl: true,
            })
        }
    }

    if (launchSurface === 'browser') {
        closeMenu?.()

        try {
            await browserShare(sharePayload)
            return
        } catch (error) {
            if (error?.name === 'AbortError') return
            console.error(
                'Failed to open browser native share sheet, falling back to share tray',
                error,
            )
        }
    }

    if (openShareTray) {
        closeMenu?.()
        openShareTray(resolvedShareUrl, { resolved: true })
        return
    }
}

// Share visibility: owners can always share their own thread (e.g. copy the
// link while it's still unpublished); everyone else needs it to be public.
// A direct message's link opens for its two participants only, so it has
// nothing to share.
export function canShareAnswer(answer) {
    const shareQuest = answer?.child_quests?.[0]
    if (isDirectMessageQuest(shareQuest)) return false
    const sharePermsTarget = shareQuest?.id ? shareQuest : answer

    return (
        perms.isPublic(sharePermsTarget) ||
        (shareQuest?.id ? perms.amQuestOwner(shareQuest) : perms.amAnswerOwner(answer))
    )
}

export default function AnswerControls(props) {
    const answer = props.answer
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(props.deleteConfirm)
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(props.archiveConfirm)
    const myRef = React.useRef(null)

    useEffect(() => {
        if (!props.popup) return

        function handleClickOutside(ev) {
            if (myRef.current && !myRef.current.contains(ev.target)) {
                ev.stopPropagation()
                ev.preventDefault()
                setTimeout(() => {
                    props.toggleControlsPopup()
                }, 100)
            }
        }

        // Bind the event listener
        document.addEventListener('mouseup', handleClickOutside)

        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mouseup', handleClickOutside)
        }
    }, [myRef])

    const { showControlButtons } = useAnswerControls(props)

    // Follow/unfollow the answer's author from the controls menu. Only
    // meaningful in the popup, for someone else's non-system post.
    const authorId = answer?.user?.id || answer?.user_id
    const followAuthorEligible =
        !!props.popup &&
        !!gon.currentUser &&
        !!authorId &&
        String(authorId) !== String(gon.currentUser.id) &&
        !answer?.is_system &&
        !props.systemQuoteNoti
    const { user: authorProfile } = useGetUser(followAuthorEligible ? String(authorId) : undefined)
    const [isTogglingFollow, setIsTogglingFollow] = useState(false)
    const authorFollowState =
        typeof authorProfile?.is_followed_by_current_user === 'boolean'
            ? authorProfile.is_followed_by_current_user
            : answer?.user?.is_followed_by_current_user
    const isFollowingAuthor = authorFollowState === true
    const hasKnownAuthorFollowState = typeof authorFollowState === 'boolean'

    const showFollowAuthor = () =>
        followAuthorEligible && hasKnownAuthorFollowState && !showConfirm() && !props.editMode

    const handleFollowAuthorToggle = async ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (isTogglingFollow || !hasKnownAuthorFollowState) return

        setIsTogglingFollow(true)
        try {
            const nextIsFollowing = !isFollowingAuthor
            const result = nextIsFollowing
                ? await api.followUser(authorId)
                : await api.unfollowUser(authorId)
            if (!result?.success) {
                throw new Error(result?.error || 'Follow update failed')
            }

            // Keep both caches in sync so profile panels and hover cards agree.
            const followerDelta = nextIsFollowing ? 1 : -1
            const nextFollowersCount = Math.max(
                0,
                (authorProfile?.followers_count ?? answer?.user?.followers_count ?? 0) +
                    followerDelta,
            )
            cache.cacheUser(
                {
                    id: authorId,
                    is_followed_by_current_user: nextIsFollowing,
                    followers_count: nextFollowersCount,
                },
                true,
            )
            queryClient.setQueryData(['user', String(authorId)], (/** @type {any} */ oldUser) => {
                if (!oldUser) return oldUser
                const oldFollowersCount =
                    typeof oldUser.followers_count === 'number' ? oldUser.followers_count : 0
                return {
                    ...oldUser,
                    is_followed_by_current_user: nextIsFollowing,
                    followers_count: Math.max(0, oldFollowersCount + followerDelta),
                }
            })
            props.toggleControlsPopup?.()
        } catch (error) {
            console.error('Error toggling follow from answer controls:', error)
        } finally {
            setIsTogglingFollow(false)
        }
    }

    const isModerationAnswerDelete = () =>
        perms.canDeleteAnswer(gon.currentUser, answer, props.quest) && !perms.amAnswerOwner(answer)

    const isModerationAnswerArchive = () =>
        !answer.archive &&
        perms.canArchiveAnswer(gon.currentUser, answer, props.quest) &&
        !perms.amAnswerOwner(answer)

    const openModerationReasonTray = ({ action, onConfirm }) => {
        const isDelete = action === 'delete'
        const { openConfirmTray } = useStore.getState().actions.trayActions

        openConfirmTray(
            `${isDelete ? 'Delete' : 'Archive'} another user's post and notify them?`,
            isDelete ? 'Delete Post' : 'Archive Post',
            reason => onConfirm(reason),
            {
                title: isDelete ? 'Delete Post' : 'Archive Post',
                cancelMessage: 'Cancel',
                reasonInput: {
                    label: `Reason for ${isDelete ? 'deletion' : 'archive'}`,
                    placeholder: 'No reason given.',
                },
            },
        )
    }

    const deleteHandler = (ev, moderationReason) => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        const answerId = props.answer.id
        props.removeAnswer(answerId)
        api.deleteAnswer(
            answerId,
            moderationReason !== undefined ? { reason: moderationReason } : {},
        )
    }

    const archiveHandler = (ev, moderationReason) => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()
        const answer = props.answer
        answer.archive ? logEv('UNARCHIVE_ANSWER') : logEv('ARCHIVE_ANSWER')

        const archive = answer.archive
        api.updateAnswer(answer.id, {
            archive: !archive,
            ...(moderationReason !== undefined ? { reason: moderationReason } : {}),
        })
        if (showArchiveConfirm && props.toggleArchiveConfirm) {
            props.toggleArchiveConfirm()
        } else {
            setShowArchiveConfirm(false)
        }
        const newAnswer = {
            ...answer,
            updated_at: new Date(),
            archive: !archive,
        }
        cache.cacheAnswer(newAnswer)
    }

    const toggleEdit = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        return props.toggleEdit()
    }

    const savePost = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        return props.savePost()
    }

    const isUserSignedIn = () => !!gon.currentUser
    const isOwner = () => gon.currentUser && gon.currentUser.id === props.answer.user_id

    const showConfirm = () => showDeleteConfirm || showArchiveConfirm

    const showEdit = () => {
        if (props.popup) return false

        return (
            !showConfirm() &&
            isUserSignedIn() &&
            isOwner() &&
            (props.editMode || showControlButtons()) &&
            !props.answer.tweet
        )
    }

    const showTweet = () => !showConfirm() && !!props.answer.tweet && showControlButtons()

    const showDelete = () =>
        perms.canDeleteAnswer(gon.currentUser, answer, props.quest) &&
        !showDeleteConfirm &&
        !showArchiveConfirm &&
        (props.popup || showControlButtons()) && // Always show in popup
        !props.editMode

    const toggleDeleteConfirm = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        if (props.toggleDeleteConfirm) props.toggleDeleteConfirm()
        else {
            setShowDeleteConfirm(!showDeleteConfirm)
            setShowArchiveConfirm(false)
        }
    }

    const requestDelete = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        if (isModerationAnswerDelete()) {
            openModerationReasonTray({
                action: 'delete',
                onConfirm: reason => deleteHandler(null, reason),
            })
            return
        }

        toggleDeleteConfirm(ev)
    }

    const showArchive = () => {
        const perm = gon.currentUser && perms.canArchiveAnswer(gon.currentUser, answer, props.quest)
        const uiPerm =
            !showArchiveConfirm && !showDeleteConfirm && showControlButtons() && !props.editMode

        return uiPerm && perm
    }

    const showCopyMessage = () => {
        const uiPerm =
            !showArchiveConfirm && !showDeleteConfirm && showControlButtons() && !props.editMode

        return uiPerm
    }

    // Star swapped places with Share: share moved up to the top controls row,
    // starring the answer's thread lives here in the menu.
    const starTargetQuest = props.answer?.child_quests?.[0]
    const isStarred = useStore(state => !!state.starredQuestIds[starTargetQuest?.id])

    const showStar = () => {
        const uiPerm =
            !showArchiveConfirm &&
            !showDeleteConfirm &&
            showControlButtons() &&
            !props.editMode &&
            props.popup

        return uiPerm && !props.systemQuoteNoti && !!starTargetQuest?.id
    }

    // Play moved out of the thread-actions block so it can sit after Star.
    const openStoryModal = useStore(state => state.actions.modalActions.openStoryModal)

    const showPlay = () => showThreadActions() && !props.quest?.is_draft && !showConfirm()

    // Illustrate/Film this post: menu-only entry points that hand the post's contents to
    // the composer as a simple post-media action referencing this post.
    const showPostMediaActions = () =>
        !!props.popup &&
        canLaunchExistingPostMediaAction(answer, gon.currentUser?.id) &&
        !showConfirm() &&
        !props.editMode &&
        !props.systemQuoteNoti &&
        showControlButtons() &&
        typeof props.onPostMediaAction === 'function' &&
        !!getAnswerDisplayText(answer)?.trim()

    const handlePostMediaAction = kind => ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!canLaunchExistingPostMediaAction(answer, gon.currentUser?.id)) return

        props.onPostMediaAction(kind)
        props.toggleControlsPopup?.()
    }

    const handlePlay = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        window.logEv('PLAY_THREAD_OPEN', {
            quest_id: props.quest?.id,
            source: 'thread_actions',
        })
        openStoryModal(props.quest)
        props.toggleControlsPopup?.()
    }

    // Onchain status moved here from the top controls row: shows the BSV tx
    // state for onchain posts, opens the explorer when a tx exists, and
    // double-click resubmits when it doesn't (same as the old top icon).
    const bsvTx = answer?.bsv_tx
    const bsvTxId = bsvTx?.tx_id

    const showOnchain = () =>
        !!props.isBsv && !showConfirm() && showControlButtons() && !props.editMode && props.popup

    const handleOnchainClick = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (bsvTxId) {
            window.open(bsvTxExplorerUrl(bsvTxId), '_blank', 'noopener,noreferrer')
            props.toggleControlsPopup?.()
        }
    }

    const handleOnchainDoubleClick = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!bsvTxId) {
            api.postToBsv(answer.id)
            props.toggleControlsPopup?.()
        }
    }

    const handleStarToggle = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!isStarred) {
            useStore.getState().set(draft => {
                draft.starredQuestIds[starTargetQuest.id] = true
            })
            api.starQuest(starTargetQuest.id)
        } else {
            useStore.getState().set(draft => {
                delete draft.starredQuestIds[starTargetQuest.id]
            })
            api.unstarQuest(starTargetQuest.id)
        }

        if (props.popup && props.toggleControlsPopup) {
            props.toggleControlsPopup()
        }
    }

    const toggleArchiveConfirm = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        if (props.toggleArchiveConfirm) props.toggleArchiveConfirm()
        else {
            setShowArchiveConfirm(!showArchiveConfirm)
            setShowDeleteConfirm(false)
        }
    }

    const requestArchive = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        if (isModerationAnswerArchive()) {
            openModerationReasonTray({
                action: 'archive',
                onConfirm: reason => archiveHandler(null, reason),
            })
            return
        }

        toggleArchiveConfirm(ev)
    }

    const showThreadActions = () => props.popup && props.isTitle && !!props.quest

    const handleCloseControls = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()
        props.toggleControlsPopup?.()
    }

    return (
        <div ref={myRef} className={cn(styles.answerControlsComp, 'answer-controls-comp')}>
            {props.popup && (
                <div className={styles.controlsModalHeader}>
                    <button
                        type="button"
                        className={styles.controlsModalCloseButton}
                        onClick={handleCloseControls}
                        aria-label="Close"
                        title="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {showOnchain() && (
                <div
                    className={cn(
                        styles.answerControl,
                        styles.onchainButtonComp,
                        props.popup && styles.popup,
                    )}
                    data-testid="answer-onchain-button"
                    onClick={handleOnchainClick}
                    onDoubleClick={handleOnchainDoubleClick}
                >
                    <div className={cn(styles.answerBtn, isMobile && styles.mobileAnswerBtn)}>
                        <BsvTransactionStatusIcon
                            bsvTx={bsvTx}
                            disableLink
                            stopPropagation={false}
                            className={styles.onchainStatusIcon}
                        />
                        {props.popup && (
                            <span className={cn('control-label', styles.controlLabel)}>
                                onchain
                            </span>
                        )}
                    </div>
                </div>
            )}

            {showFollowAuthor() && (
                <div
                    className={cn(
                        styles.answerControl,
                        styles.followAuthorButtonComp,
                        props.popup && styles.popup,
                    )}
                    data-testid="answer-follow-author-button"
                    onClick={handleFollowAuthorToggle}
                >
                    <WithTooltip
                        tip={isFollowingAuthor ? 'Unfollow this author.' : 'Follow this author.'}
                        offset={0}
                    >
                        <div className={cn(styles.answerBtn, isMobile && styles.mobileAnswerBtn)}>
                            {isFollowingAuthor ? <UserCheck size={16} /> : <UserPlus size={16} />}
                            {props.popup && (
                                <span className={cn('control-label', styles.controlLabel)}>
                                    {`${isFollowingAuthor ? 'unfollow' : 'follow'} ${
                                        answer?.user?.username || answer?.user?.name || 'author'
                                    }`}
                                </span>
                            )}
                        </div>
                    </WithTooltip>
                </div>
            )}

            {showThreadActions() && (
                <div>
                    <ExtendedOptions
                        compact
                        hideHeader
                        hideDeleteOption
                        hidePlayOption
                        hideMuteOptions
                        listContextStyles={styles.inlineOptionList}
                        quest={props.quest}
                        filter={props.filter}
                        deleteHandler={props.deleteQuest}
                        muteQuestUser={props.muteQuestUser}
                        reloadQuest={props.reloadQuest}
                        tweet={props.tweet}
                        close={props.toggleControlsPopup}
                    />
                </div>
            )}

            {showStar() && (
                <div
                    className={cn(
                        styles.answerControl,
                        styles.starButtonComp,
                        props.popup && styles.popup,
                    )}
                    data-testid="answer-star-button"
                    onClick={handleStarToggle}
                >
                    <WithTooltip
                        tip={
                            isStarred
                                ? 'Remove from your starred stream.'
                                : 'Add to your starred stream.'
                        }
                        offset={0}
                    >
                        <div className={cn(styles.answerBtn, isMobile && styles.mobileAnswerBtn)}>
                            <Star size={16} fill={isStarred ? 'currentColor' : 'none'} />
                            {props.popup && (
                                <span className={cn('control-label', styles.controlLabel)}>
                                    {isStarred ? 'unstar' : 'star'}
                                </span>
                            )}
                        </div>
                    </WithTooltip>
                </div>
            )}

            {showPlay() && (
                <div
                    className={cn(
                        styles.answerControl,
                        styles.playButtonComp,
                        props.popup && styles.popup,
                    )}
                    data-testid="answer-play-button"
                    onClick={handlePlay}
                >
                    <WithTooltip tip="Play this thread." offset={0}>
                        <div className={cn(styles.answerBtn, isMobile && styles.mobileAnswerBtn)}>
                            <Play size={16} />
                            {props.popup && (
                                <span className={cn('control-label', styles.controlLabel)}>
                                    play
                                </span>
                            )}
                        </div>
                    </WithTooltip>
                </div>
            )}

            {showPostMediaActions() && (
                <div
                    className={cn(
                        styles.answerControl,
                        styles.postMediaButtonComp,
                        props.popup && styles.popup,
                    )}
                    data-testid="answer-illustrate-button"
                    onClick={handlePostMediaAction('illustrate')}
                >
                    <WithTooltip tip="Create an AI image from this post." offset={0}>
                        <div className={cn(styles.answerBtn, isMobile && styles.mobileAnswerBtn)}>
                            <Paintbrush size={16} />
                            {props.popup && (
                                <span className={cn('control-label', styles.controlLabel)}>
                                    illustrate
                                </span>
                            )}
                        </div>
                    </WithTooltip>
                </div>
            )}

            {showPostMediaActions() && (
                <div
                    className={cn(
                        styles.answerControl,
                        styles.postMediaButtonComp,
                        props.popup && styles.popup,
                    )}
                    data-testid="answer-film-button"
                    onClick={handlePostMediaAction('film')}
                >
                    <WithTooltip tip="Create an AI film from this post." offset={0}>
                        <div className={cn(styles.answerBtn, isMobile && styles.mobileAnswerBtn)}>
                            <Film size={16} />
                            {props.popup && (
                                <span className={cn('control-label', styles.controlLabel)}>
                                    film
                                </span>
                            )}
                        </div>
                    </WithTooltip>
                </div>
            )}

            {showCopyMessage() && (
                <CopyMessageButton
                    copyContent={props.copyContent}
                    popup={props.popup}
                    showLabel={props.popup}
                    label="copy text"
                    tip="Copy message text."
                />
            )}

            {showTweet() && (
                <TweetedButton
                    popup={props.popup}
                    showLabel={props.popup}
                    label="This message was tweeted"
                    tip="Tweeted message."
                />
            )}

            {showEdit() && (
                <Edit
                    quest={props.quest}
                    answer={props.answer}
                    answerHover={props.answerHover}
                    saving={props.saving}
                    editMode={props.editMode}
                    toggleEdit={toggleEdit}
                    savePost={savePost}
                    setImageDownload={props.setImageDownload}
                    content={props.content}
                    imgHandler={props.imgHandler}
                    fileHandler={props.fileHandler}
                    videoHandler={props.videoHandler}
                    mobileVideoHandler={props.mobileVideoHandler}
                    clearEdits={props.clearEdits}
                    uploadBlocked={props.uploadBlocked}
                />
            )}

            {showThreadActions() && (
                <ExtendedOptions
                    compact
                    hideHeader
                    onlyMuteOptions
                    listContextStyles={styles.inlineOptionList}
                    quest={props.quest}
                    filter={props.filter}
                    muteQuestUser={props.muteQuestUser}
                    reloadQuest={props.reloadQuest}
                    close={props.toggleControlsPopup}
                />
            )}

            {showArchive() && (
                <ArchiveButton
                    archived={answer.archive}
                    toggleArchiveConfirm={requestArchive}
                    popup={props.popup}
                    showLabel={props.popup}
                />
            )}

            {showArchiveConfirm && (
                <ConfirmArchive
                    archived={answer?.archive}
                    toggleArchiveConfirm={toggleArchiveConfirm}
                    handleArchive={archiveHandler}
                />
            )}

            {showDelete() && (
                <DeleteButton
                    toggleDeleteConfirm={requestDelete}
                    popup={props.popup}
                    showLabel={props.popup}
                    label="delete"
                />
            )}

            {showDeleteConfirm && (
                <ConfirmDelete
                    toggleDeleteConfirm={toggleDeleteConfirm}
                    deleteHandler={deleteHandler}
                />
            )}
        </div>
    )
}

export function AnswerShareTray({
    answer,
    copyContent: _copyContent,
    isOpen,
    onDismiss,
    overlayClassName = undefined,
    shareUrl,
    rewardEnabled = false,
    linkReady = true,
}) {
    const exchangeRate = useStore(state => state.exchangeRate)
    const shareText = useMemo(
        () => buildShareText(answer, exchangeRate),
        [answer?.bsv_upvalue_amt, exchangeRate],
    )
    const shareQuest = answer?.child_quests?.[0]
    const sharePermsTarget = shareQuest?.id ? shareQuest : answer
    const canShareImageCard = perms.isPublic(sharePermsTarget)
    const shareCardUrl =
        typeof window === 'undefined' ? null : getAnswerShareCardUrl(answer, SHARE_CARD_PRESET)

    return (
        <ShareTray
            canShareImageCard={canShareImageCard}
            closeButtonTestId="answer-share-tray-close"
            dialogTestId="answer-share-tray"
            isOpen={isOpen}
            linkReady={linkReady}
            onDismiss={onDismiss}
            overlayClassName={overlayClassName}
            rewardEnabled={rewardEnabled}
            shareCardId={answer?.id}
            shareCardUrl={shareCardUrl}
            shareKind="post"
            shareText={shareText}
            shareTitle={buildShareTitle(answer)}
            shareUrl={shareUrl}
        />
    )
}

export function ArchiveButton(props) {
    const archived = props.archived
    const label = archived ? 'unarchive' : 'archive'
    const tip = archived ? 'Unarchive' : 'Archive'

    const mobileAnswerBtn = isMobile ? styles.mobileAnswerBtn : null
    const popupStyles = props.popup ? styles.popup : null

    const contextStyles = props.contextStyles ? props.contextStyles : null

    return (
        <div
            className={cn(
                styles.answerControl,
                styles.archiveButtonComp,
                popupStyles,
                contextStyles,
            )}
            onClick={props.toggleArchiveConfirm}
        >
            <WithTooltip tip={tip} offset={0}>
                <div className={cn(styles.answerBtn, mobileAnswerBtn)}>
                    {archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}

                    {props.showLabel && (
                        <span className={cn('control-label', styles.controlLabel)}>{label}</span>
                    )}
                </div>
            </WithTooltip>
        </div>
    )
}

function TweetedButton(props) {
    const mobileAnswerBtn = isMobile ? styles.mobileAnswerBtn : null
    const popupStyles = props.popup ? styles.popup : null

    return (
        <div
            className={cn(styles.answerControl, styles.tweetedButtonComp, popupStyles)}
            onClick={e => {
                e.preventDefault()
                e.stopPropagation()
            }}
        >
            <WithTooltip tip={props.tip} offset={0}>
                <div className={cn(styles.answerBtn, mobileAnswerBtn)}>
                    <Send size={16} className={styles.twitterLabel} />

                    {props.showLabel && (
                        <span className={cn('control-label', styles.controlLabel)}>
                            {props.label}
                        </span>
                    )}
                </div>
            </WithTooltip>
        </div>
    )
}

export function CopyMessageButton(props) {
    const [copied, setCopied] = useState(false)
    const mobileAnswerBtn = isMobile ? styles.mobileAnswerBtn : null
    const popupStyles = props.popup ? styles.popup : null

    React.useEffect(() => {
        if (!copied) return

        const timeoutId = window.setTimeout(() => setCopied(false), 1500)
        return () => window.clearTimeout(timeoutId)
    }, [copied])

    return (
        <CopyButton
            copyContent={props.copyContent}
            tip={props.tip}
            contextStyles={cn(styles.answerControl, styles.copyMessageButtonComp, popupStyles)}
            onCopy={() => setCopied(true)}
        >
            <div className={cn(styles.answerBtn, mobileAnswerBtn)}>
                <Copy size={16} />

                {props.showLabel && (
                    <span className={cn('control-label', styles.controlLabel)}>
                        {copied ? 'copied!' : props.label}
                    </span>
                )}
            </div>
        </CopyButton>
    )
}

function DeleteButton(props) {
    const mobileAnswerBtn = isMobile ? styles.mobileAnswerBtn : null
    const popupStyles = props.popup ? styles.popup : null

    return (
        <div className={cn(styles.answerControl, styles.deleteButtonComp, popupStyles)}>
            <WithTooltip tip="Delete." offset={0}>
                <div
                    className={cn(styles.answerBtn, mobileAnswerBtn)}
                    onClick={props.toggleDeleteConfirm}
                >
                    <Trash2 size={16} />

                    {props.showLabel && (
                        <span className={cn('control-label', styles.controlLabel)}>
                            {props.label}
                        </span>
                    )}
                </div>
            </WithTooltip>
        </div>
    )
}

export function ConfirmArchive(props) {
    const archived = props.archived
    const toggleArchiveConfirm = props.toggleArchiveConfirm
    const archiveHandler = props.handleArchive

    return (
        <div className={styles.confirmComp}>
            <div className={styles.cancelBtn} onClick={toggleArchiveConfirm}>
                <X size={16} />

                <span
                    className={cn(
                        styles.controlLabel,
                        styles.confirmLabel,
                        'control-label confirm-label',
                    )}
                >
                    cancel
                </span>
            </div>

            <div
                className={cn('archive-confirm-btn pull-right', styles.archiveConfirmBtn)}
                onClick={archiveHandler}
            >
                {archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                {archived ? (
                    <span className="archive-confirm-label confirm-label">unarchive</span>
                ) : (
                    <span className="archive-confirm-label confirm-label">archive</span>
                )}
            </div>
        </div>
    )
}

function ConfirmDelete(props) {
    const toggleDeleteConfirm = props.toggleDeleteConfirm
    const deleteHandler = props.deleteHandler

    return (
        <div className={styles.confirmComp}>
            <div className={styles.cancelBtn} onClick={toggleDeleteConfirm}>
                <X size={16} />

                <span
                    className={cn(
                        styles.controlLabel,
                        styles.confirmLabel,
                        'control-label confirm-label',
                    )}
                >
                    cancel
                </span>
            </div>

            <div className="delete-confirm-btn" onClick={deleteHandler}>
                <Trash2 size={16} />
                <span className="delete-confirm-label confirm-label">delete</span>
            </div>
        </div>
    )
}
